import { ComboboxDemo } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { PageNavigationHeader } from "@/features/license-fee/list/layouts/page-navigation-header";
import { useCompanySelection } from "@/hooks/useCompanySelection";
import { debounce } from "@/utils/debounce";
import { getInputProps, useForm } from "@conform-to/react";
import { useNavigate, Form, useLoaderData } from "react-router-dom";
import { parseWithZod } from "@conform-to/zod";
import {
  schema,
  createSchema,
} from "./private-office-contracts-create.validation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/inputs/input";
import { usePackageSelection } from "@/hooks/usePackageSelection";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useFilters } from "@/hooks/use-filters";
import { LoaderData } from "./loader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUserSelection } from "@/hooks/useUserSelection";
import { MoneyInput } from "@/components/inputs/money-input/money-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardTitle,
  CardDescription,
  CardHeader,
  CardContent,
} from "@/components/ui/card";
import { usePrivateOfficeAssetsDropzone } from "@/hooks/usePrivateOfficeAssetsDropzone";
import { PoAssetsDropzone } from "@/components/po-assets-dropzone";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio";
import { poTypeOptions } from "@/features/license-fee/list/utils/private-office-contract-mappings";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { SirenIcon } from "lucide-react";
import { useGetPackages } from "@/hooks/use-get-packages/use-get-packages";
import { formatInTimeZone } from "date-fns-tz";
import { addDays, format, parse } from "date-fns";
import { CalcCreditsToConsume } from "@/features/private-room/bookings/adapters/get-privateroom-price-by-company-package";
import { getSpaceTakeValue } from "@/utils/get-space-take-value/get-space-take-value";
import { apiHubspotIntegrationMs } from "@/services/api-hubspot-integration-ms";
import { useBookingFlow } from "@/features/private-room/bookings/context/use-booking-flow/use-booking-flow";
import { z } from "zod";
import { parseMoneyValue } from "@/utils/parse-money";
import {
  TAKE_RATE_EXCEPTION_FORM_FIELDS,
  isTakeRateException,
} from "@/features/private-room/bookings/adapters/take-rate-exception";
import { TakeRateExceptionFields } from "@/features/private-room/bookings/layouts/take-rate-exception-fields";
import { PrivateOfficeTypes } from "@/hooks/use-filters/use-filters.types";
import { ConvertPackageDuration } from "@/utils/convert-package-duration";
import { ConvertProductToName } from "@/utils/convert-product-to-name";
import { CalculateCycleConsumption } from "@/utils/calculate-cycle-consumption";

type FormData = z.infer<typeof schema>;

const validateHubspotDealId = async (dealId: string): Promise<boolean> => {
  try {
    const response = await apiHubspotIntegrationMs.get(`/deals/${dealId}`);
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

export default function PrivateOfficeContractsCreate() {
  const navigate = useNavigate();
  const { contractTypeOptions, renewalIndicatorOptions } =
    useLoaderData() as LoaderData;
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null,
  );
  const [minEndDate, setMinEndDate] = useState<string | null>(null);
  const [selectedPrivateOffice, setSelectedPrivateOffice] =
    useState<PrivateOfficeTypes>();
  const [dealIdError, setDealIdError] = useState<string | null>(null);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const endDateInputRef = useRef<HTMLInputElement>(null);
  const [isUndeterminedTime, setIsUndeterminedTime] = useState(false);
  const [creditConsumption, setCreditConsumption] = useState<number>(0);
  const [availableCycleCredits, setAvailableCycleCredits] = useState<number>(0);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | null>(
    null,
  );
  const [selectedSpaceName, setSelectedSpaceName] = useState<string | null>(
    null,
  );

  const {
    setSelectedPackage,
    setSelectedCycle,
    setBookingData,
    bookingData,
    handleSubmitPoContractData,
    poContractCreateData,
    setSelectedCompany,
  } = useBookingFlow();

  const [form, fields] = useForm({
    defaultValue: poContractCreateData
      ? {
          ...poContractCreateData,
          partnerValue: parseMoneyValue(
            poContractCreateData.partnerValue,
          ).toString(),
        }
      : {},
    onValidate({ formData }) {
      const result = parseWithZod(formData, {
        schema: createSchema(),
      });
      if (formData.get("dealId") && dealIdError) {
        return {
          ...result,
          status: "error",
          payload: result.payload,
          error: { dealId: [dealIdError] },
          reply: result.reply,
        };
      }
      if (formData.get("startDate") && startDateError) {
        return {
          ...result,
          status: "error",
          payload: result.payload,
          error: { startDate: [startDateError] },
          reply: result.reply,
        };
      }
      return result;
    },
    shouldValidate: "onBlur",
    shouldRevalidate: "onInput",
  });

  const {
    myFiles: acceptanceTermFiles,
    onDrop: onAcceptanceTermDrop,
    removeAt: removeAcceptanceTermFile,
    loading: acceptanceTermLoading,
  } = usePrivateOfficeAssetsDropzone({
    maxFiles: 1,
    assetType: "partner_assignment_term",
    form,
    fieldName: fields.acceptanceTerm.name,
    defaultValues: poContractCreateData?.acceptanceTermFiles,
  });

  const {
    myFiles: knowledgeTermFiles,
    onDrop: onKnowledgeTermDrop,
    removeAt: removeKnowledgeTermFile,
    loading: knowledgeTermLoading,
  } = usePrivateOfficeAssetsDropzone({
    maxFiles: 1,
    assetType: "company_assignment_term",
    form,
    fieldName: fields.knowledgeTerm.name,
    defaultValues: poContractCreateData?.knowledgeTermFiles,
  });

  const {
    handSelectedSpace,
    handSelectedPrivateOffice,
    filterSpaces,
    privateOfficeData,
  } = useFilters();

  const { companyUsers, handleSearchUsersByCompanyId } = useUserSelection({});

  const {
    companies,
    companyPackages,
    handleSearchCompanies,
    handleCompanySelect,
  } = useCompanySelection({});

  const { filteredPackages, handleSearchPackages, setPackages } =
    usePackageSelection({ defaultPackages: companyPackages });

  const {
    getActiveCycle,
    getPackageDetails,
    packageDetails,
    activeCycle,
    activeUnitPrice,
  } = useGetPackages();

  const filteredPrivateOfficesByStatus = useMemo(() => {
    if (!privateOfficeData) return [];

    const allowOccupied =
      fields.contractType.value === "extra-service" ||
      fields.contractType.value === "tax_address";

    return privateOfficeData.filter((office: PrivateOfficeTypes) => {
      if (office.status === "removed") return false;
      if (office.status === "unavailable") return false;
      if (office.status === "occupied") return allowOccupied;
      return true;
    });
  }, [privateOfficeData, fields.contractType.value]);

  const handlePackageSelect = useCallback(
    (packageId: string) => {
      setSelectedPackageId(packageId);
      getPackageDetails(Number(packageId), Number(fields.companyId.value));
    },
    [companyPackages, fields.companyId.value],
  );

  const handlePrivateRoomSelect = useCallback(
    (privateOfficeId: string) => {
      const privateRoom = privateOfficeData?.find(
        (office: PrivateOfficeTypes) => office.id === privateOfficeId,
      );
      setSelectedPrivateOffice(privateRoom);
    },
    [privateOfficeData],
  );

  const handleStartDateSelect = useCallback(
    async (startDate: string) => {
      if (fields.companyId.value && selectedPackageId) {
        await getActiveCycle(
          startDate,
          Number(selectedPackageId),
          Number(fields.companyId.value),
          false,
        );
        form.validate({ name: fields.startDate.name });
      }
    },
    [selectedPackageId, fields.companyId.value],
  );

  const calculateMinEndDate = useCallback(
    (startDate: string) => {
      if (!startDate || !fields.noticeInDaysClient.value) return;

      const parsedStartDate = parse(startDate, "yyyy-MM-dd", new Date());
      const noticeDays = Number(fields.noticeInDaysClient.value);
      const minEndDate = addDays(parsedStartDate, noticeDays);

      setMinEndDate(format(minEndDate, "yyyy-MM-dd"));
      if (
        fields.endDate.value &&
        fields.endDate.value < format(minEndDate, "yyyy-MM-dd")
      ) {
        form.update({
          name: fields.endDate.name,
          value: format(minEndDate, "yyyy-MM-dd"),
        });
      }
    },
    [
      fields.noticeInDaysClient.value,
      fields.startDate.value,
      fields.endDate.value,
    ],
  );

  const calculateRepass = useCallback(
    (takeRate: number) => {
      if (!Number.isFinite(takeRate) || takeRate <= 0) {
        form.update({
          name: fields.repass.name,
          value: 0,
        });
        return;
      }

      const partnerValue = parseMoneyValue(form.value?.partnerValue ?? "0");
      const takeFee = partnerValue * (takeRate / 100);
      const repass = partnerValue - takeFee;
      form.update({
        name: fields.repass.name,
        value: repass.toFixed(2),
      });
    },
    [form.value?.partnerValue],
  );

  const handleSpaceSelect = async (spaceId: string) => {
    handSelectedPrivateOffice(spaceId);
    const takeRate = await getSpaceTakeValue(Number(spaceId));
    if (takeRate !== undefined) {
      form.update({
        name: fields.takeRatePercentage.name,
        value: Number(takeRate),
      });
    }
    const selectedSpace = filterSpaces?.find(
      (space) => space.id === Number(spaceId),
    );
    setSelectedSpaceName(selectedSpace?.name || null);
  };

  const handleSubmit = useCallback(() => {
    form.validate();
    if (form.valid && form.value && activeCycle && !dealIdError) {
      setSelectedCycle(activeCycle);
      packageDetails &&
        setSelectedPackage({
          ...packageDetails,
          groups: packageDetails.groups.map((group) => group.id),
        });

      const formData = form.value;

      const selectedCompany = companies.find(
        (company) => company.value === formData.companyId,
      );

      const selectedSpace = filterSpaces?.find(
        (space) => space.id === Number(formData.coworkingId),
      );

      setSelectedCompany({ id: Number(selectedCompany?.value) });

      setBookingData({
        company: {
          id: formData.companyId,
          name: selectedCompany?.label,
        },
        package: {
          id: formData.packageId,
          name: packageDetails?.name,
        },
        private_room: {
          id: formData.privateOfficeId,
          searchId: selectedPrivateOffice?.searchId,
          name: selectedPrivateOffice
            ? selectedPrivateOffice.label
            : bookingData?.private_room.name,
        },
        start_date: formData.startDate,
        end_date: formData.endDate,
        coworking: selectedSpace,
        credits: formData.monthlyCredits,
      });

      handleSubmitPoContractData({
        ...formData,
        members: Number(formData.members),
        monthlyCredits: parseFloat(formData.monthlyCredits!),
        takeRatePercentage: parseFloat(formData.takeRatePercentage!),
        repass: Number(formData.repass),
        noticeInDaysClient: Number(formData.noticeInDaysClient),
        noticeInDaysPartner: Number(formData.noticeInDaysPartner),
        expectedDurationMonths: Number(formData.expectedDurationMonths),
        hasUndeterminedTime: Boolean(formData.hasUndeterminedTime),
        acceptanceTermFiles,
        knowledgeTermFiles,
      } as FormData & {
        acceptanceTermFiles: { id: string; url: string; name: string }[];
        knowledgeTermFiles: { id: string; url: string; name: string }[];
      });
    }
  }, [
    form,
    activeCycle,
    setSelectedCycle,
    packageDetails,
    setSelectedPackage,
    setBookingData,
    handleSubmitPoContractData,
    selectedPrivateOffice?.label,
  ]);

  const calculateMonthlyCredits = useCallback(() => {
    if (activeUnitPrice && fields.partnerValue.value) {
      const partnerValueNumber = parseMoneyValue(fields.partnerValue.value);

      // allowFractional keeps this in sync with the update flow
      // (private-office-contracts-update.tsx): rounding up here produced a
      // different monthlyCredits for the same price on create vs. edit.
      const calculatedCredits = CalcCreditsToConsume(
        activeUnitPrice,
        partnerValueNumber,
        { allowFractional: true },
      );

      if (calculatedCredits !== undefined) {
        form.update({
          name: fields.monthlyCredits.name,
          value: calculatedCredits,
        });
      }
    }
  }, [activeUnitPrice, fields.partnerValue.value]);

  useEffect(() => {
    calculateMonthlyCredits();
  }, [activeUnitPrice, selectedPrivateOffice]);

  useEffect(() => {
    if (activeCycle) {
      const { newCreditConsumption, newCreditAmount } =
        CalculateCycleConsumption(
          activeCycle,
          Number(fields.monthlyCredits.value) || 0,
        );

      setCreditConsumption(newCreditConsumption);
      setAvailableCycleCredits(newCreditAmount - newCreditConsumption);
    }
  }, [activeCycle, fields.monthlyCredits.value]);

  useEffect(() => {
    setPackages(companyPackages);
  }, [companyPackages]);

  useEffect(() => {
    if (!activeCycle && form.value?.startDate) {
      setStartDateError(
        "Não foi possível encontrar um ciclo ativo para a data selecionada",
      );
    } else {
      setStartDateError(null);
    }
  }, [activeCycle, form.value?.startDate]);

  useEffect(() => {
    if (form.value?.partnerValue && form.value?.takeRatePercentage) {
      calculateRepass(Number(form.value?.takeRatePercentage));
    }
  }, [form.value?.partnerValue, form.value?.takeRatePercentage]);

  useEffect(() => {
    if (selectedPackageId && fields.startDate.value) {
      getActiveCycle(
        fields.startDate.value,
        Number(selectedPackageId),
        Number(fields.companyId.value),
        false,
      );
    }
  }, [selectedPackageId]);

  useEffect(() => {
    if (
      fields.contractType.value === "tax_address" ||
      fields.contractType.value === "fixed_position"
    ) {
      form.update({
        name: fields.privateOfficeId.name,
        value: "",
      });

      setSelectedPrivateOffice(undefined);
      setBookingData({
        ...bookingData,
        private_room: {
          id: undefined,
          searchId: undefined,
          name: undefined,
        },
      });
    }
  }, [fields.contractType.value]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (poContractCreateData && bookingData) {
        const newValue = !poContractCreateData.endDate;
        setIsUndeterminedTime(newValue);
        form.update({
          name: fields.hasUndeterminedTime.name,
          value: newValue,
        });

        handleSearchCompanies(bookingData.company.name);
        handleCompanySelect({
          companyId: poContractCreateData.companyId,
          hasFilterAvailability: true,
        });
        handleSearchUsersByCompanyId(poContractCreateData.companyId);
        setSelectedCompany(bookingData.company);
        form.update({
          name: fields.companyId.name,
          value: poContractCreateData.companyId,
        });

        handleSearchPackages(bookingData.package.name);
        setSelectedPackageId(poContractCreateData.packageId);
        handlePackageSelect(poContractCreateData.packageId);
        form.update({
          name: fields.packageId.name,
          value: poContractCreateData.packageId,
        });

        await handSelectedSpace(bookingData.coworking.name);
        handleSpaceSelect(poContractCreateData.coworkingId);
        form.update({
          name: fields.coworkingId.name,
          value: poContractCreateData.coworkingId,
        });
        await handSelectedPrivateOffice(poContractCreateData.coworkingId);
        form.update({
          name: fields.privateOfficeId.name,
          value: poContractCreateData.privateOfficeId,
        });
      }
    };

    loadInitialData();
  }, []);

  return (
    <div className=" gap-6 flex flex-col items-center bg-slate-50">
      <div className="self-stretch px-8 pb-8 relative flex justify-center items-start gap-8">
        <Form
          className="max-w-[1000px] flex flex-col gap-8"
          method="POST"
          id={form.id}
          onSubmit={form.onSubmit}
        >
          <div className="w-full max-w-[1000px]">
            <PageNavigationHeader
              onClick={() =>
                navigate(DEFAULT_ROUTES.PRIVATE_OFFICE_CONTRACTS_LIST)
              }
              title="Criando novo contrato de venda de escritório"
              subtitle={
                <>
                  Associe um escritório privativo à uma empresa contrante.{" "}
                  <span className="font-medium">
                    Campos com asterísco * são obrigatórios.
                  </span>
                </>
              }
            />
          </div>
          <div className="flex flex-col justify-start items-center gap-8">
            <Card className="w-full gap-6">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Tipo de Contrato
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Selecione o tipo de contrato a ser criado.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col">
                <div className="flex items-start gap-4">
                  <div className="h-16 gap-2 w-1/2">
                    <div className="flex flex-col gap-1">
                      <Select
                        key={fields.contractType.key}
                        name={fields.contractType.name}
                        defaultValue={fields.contractType.value as string}
                        onValueChange={(value) => {
                          form.update({
                            name: fields.contractType.name,
                            value,
                          });
                          const selectElement =
                            document.activeElement as HTMLElement;
                          if (selectElement) selectElement.blur();

                          const newValue = value === "po";
                          setIsUndeterminedTime(newValue);
                          form.update({
                            name: fields.hasUndeterminedTime.name,
                            value: newValue,
                          });
                          if (value !== "po") {
                            form.update({
                              name: fields.poType.name,
                              value: undefined,
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                          <SelectValue placeholder="Selecione um tipo de contrato" />
                        </SelectTrigger>
                        <SelectContent>
                          {contractTypeOptions.map((contractType) => (
                            <SelectItem
                              key={contractType.value}
                              value={contractType.value}
                            >
                              {contractType.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-red-500 text-sm">
                        {fields.contractType.errors}
                      </p>
                    </div>
                  </div>
                </div>
                {fields.contractType.value === "po" && (
                  <div className="flex flex-col gap-2 mt-4">
                    <Label className="text-sm font-medium">
                      Tipo de escritório *
                    </Label>
                    <RadioGroup
                      key={fields.poType.key}
                      name={fields.poType.name}
                      value={
                        (fields.poType.value as string | undefined) ||
                        (poContractCreateData?.poType as string | undefined) ||
                        ""
                      }
                      onValueChange={(value) => {
                        form.update({ name: fields.poType.name, value });
                      }}
                      className="flex gap-4"
                    >
                      {poTypeOptions.map((option) => (
                        <div
                          key={option.value}
                          className="flex items-center space-x-2"
                        >
                          <RadioGroupItem
                            value={option.value}
                            id={option.id}
                            className="border-black text-black"
                          />
                          <Label
                            htmlFor={option.id}
                            className="text-sm font-medium leading-none cursor-pointer"
                          >
                            {option.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    <p className="text-red-500 text-sm">
                      {fields.poType.errors}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="w-full gap-6">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Empresa Contratante
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Identifique a empresa responsável por esta contratação e o
                  pacote a ser utilizado.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="self-stretch flex flex-col justify-start items-start gap-4">
                  <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="flex-1">
                      <div className="flex flex-col gap-1 min-h-[72px]">
                        <Label>Empresa *</Label>
                        <div className="flex flex-col gap-1">
                          <ComboboxDemo
                            key={fields.companyId.key}
                            name={fields.companyId.name}
                            emptyPlaceholder="Empresa não encontrada"
                            searchPlaceholder="Busque uma empresa"
                            selectPlaceholder="Busque por nome ou ID"
                            defaultValue={fields.companyId.value}
                            onChange={debounce(handleSearchCompanies, 500)}
                            items={companies}
                            minSearchLength={3}
                            onSelect={(item) => {
                              handleCompanySelect({
                                companyId: item,
                                hasFilterAvailability: true,
                              });
                              handleSearchUsersByCompanyId(item);
                              const selectedCompany = companies.find(
                                (company) => company.value === item,
                              );
                              setSelectedCompanyName(
                                selectedCompany?.label || null,
                              );
                            }}
                            shouldFilter={false}
                          />
                          <p className="text-red-500 text-sm">
                            {fields.companyId.errors}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col gap-1 min-h-[72px]">
                        <Label>Deal ID (Hubspot) *</Label>
                        <div className="flex flex-col gap-1">
                          <Input
                            label="Deal ID (Hubspot)"
                            hasRemoveTopMargin={true}
                            showlabel={false}
                            placeholder="Adicione o Deal ID"
                            {...getInputProps(fields.dealId, { type: "text" })}
                            onBlur={async (e) => {
                              const dealId = e.target.value;
                              if (!dealId) {
                                setDealIdError(null);
                                return;
                              }

                              const isValid =
                                await validateHubspotDealId(dealId);
                              if (!isValid) {
                                setDealIdError(
                                  "Deal ID inválido ou não encontrado no Hubspot",
                                );
                                form.validate({ name: fields.dealId.name });
                              } else {
                                setDealIdError(null);
                              }
                            }}
                            error={dealIdError || fields.dealId.errors}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col gap-1 min-h-[72px]">
                        <Label>Pacote *</Label>
                        <div className="flex flex-col gap-1">
                          <ComboboxDemo
                            key={fields.packageId.key}
                            name={fields.packageId.name}
                            emptyPlaceholder="Pacote não encontrado"
                            searchPlaceholder="Busque um pacote"
                            selectPlaceholder="Pacote"
                            defaultValue={fields.packageId.value}
                            items={filteredPackages}
                            onSelect={(item) => {
                              handlePackageSelect(item);
                            }}
                            disabled={!fields.companyId.value}
                          />
                          <p className="text-red-500 text-sm">
                            {fields.packageId.errors}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-start items-start gap-4">
                  <div className="flex-1 h-16 inline-flex flex-col justify-start items-start gap-2">
                    <Label>Pessoa responsável pelo contrato (cliente)</Label>
                    <div className="self-stretch flex flex-col justify-start items-start gap-1">
                      <Select
                        key={fields.contractResponsibleUser.key}
                        name={fields.contractResponsibleUser.name}
                        defaultValue={fields.contractResponsibleUser.value}
                        onValueChange={(value) => {
                          form.update({
                            name: fields.contractResponsibleUser.name,
                            value,
                          });
                          const selectElement =
                            document.activeElement as HTMLElement;
                          if (selectElement) selectElement.blur();
                        }}
                      >
                        <SelectTrigger className="w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                          <SelectValue placeholder="Selecione uma pessoa responsável pelo contrato" />
                        </SelectTrigger>
                        <SelectContent>
                          {companyUsers.length === 0 ? (
                            <SelectItem value="no-users" disabled>
                              Empresa não selecionada ou usuários não
                              encontrados
                            </SelectItem>
                          ) : (
                            companyUsers.map((user) => (
                              <SelectItem key={user.value} value={user.value}>
                                {`${user.value} - ${user.label}`}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-red-500 text-sm">
                        {fields.contractResponsibleUser.errors}
                      </p>
                    </div>
                  </div>
                  <div className="flex-1 h-16 inline-flex flex-col justify-start items-start gap-2">
                    <Label>
                      Pessoa responsável por gestão das salas (cliente)
                    </Label>
                    <div className="self-stretch flex flex-col justify-start items-start gap-1">
                      <Select
                        key={fields.roomResponsibleUser.key}
                        name={fields.roomResponsibleUser.name}
                        defaultValue={fields.roomResponsibleUser.value}
                        onValueChange={(value) => {
                          form.update({
                            name: fields.roomResponsibleUser.name,
                            value,
                          });
                          const selectElement =
                            document.activeElement as HTMLElement;
                          if (selectElement) selectElement.blur();
                        }}
                      >
                        <SelectTrigger className="w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                          <SelectValue placeholder="Selecione uma pessoa responsável pelas salas" />
                        </SelectTrigger>
                        <SelectContent>
                          {companyUsers.length === 0 ? (
                            <SelectItem value="no-users" disabled>
                              Empresa não selecionada ou usuários não
                              encontrados
                            </SelectItem>
                          ) : (
                            companyUsers.map((user) => (
                              <SelectItem key={user.value} value={user.value}>
                                {`${user.value} - ${user.label}`}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-red-500 text-sm">
                        {fields.roomResponsibleUser.errors}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="w-full gap-6">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Espaço Contratado
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Selecione o espaço parceiro e o escritório privativo que será
                  reservado.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="self-stretch flex flex-col justify-start items-start gap-4">
                  <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="flex-1 h-16 inline-flex flex-col justify-start items-start gap-2">
                      <Label>Espaço *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-1">
                        <ComboboxDemo
                          key={fields.coworkingId.key}
                          name={fields.coworkingId.name}
                          emptyPlaceholder="Espaço não encontrado"
                          searchPlaceholder="Busque um espaço"
                          selectPlaceholder="Espaço"
                          defaultValue={fields.coworkingId.value}
                          onChange={debounce(handSelectedSpace, 500)}
                          items={(filterSpaces || []).map(
                            (space: { id: number; name: string }) => ({
                              value: space.id.toString(),
                              label: space.name,
                            }),
                          )}
                          onSelect={handleSpaceSelect}
                          shouldFilter={false}
                        />
                        <p className="text-red-500 text-sm">
                          {fields.coworkingId.errors}
                        </p>
                      </div>
                    </div>
                    {fields.contractType.value !== "fixed_position" && (
                      <div className="flex-1 h-16 w-1/2 inline-flex flex-col justify-start items-start gap-2">
                        <Label>Escritório Privativo *</Label>
                        <div className="self-stretch flex flex-col justify-start items-start gap-1">
                          <ComboboxDemo
                            key={fields.privateOfficeId.key}
                            name={fields.privateOfficeId.name}
                            className={
                              "disabled:ring-zinc-100 disabled:bg-gray-200 disabled:border disabled:border-gray-200"
                            }
                            emptyPlaceholder="Escritório não encontrado"
                            searchPlaceholder="Busque um escritório"
                            selectPlaceholder="Escritório"
                            defaultValue={fields.privateOfficeId.value}
                            onSelect={(item) => {
                              handlePrivateRoomSelect(item);
                            }}
                            items={filteredPrivateOfficesByStatus.map(
                              (office) => ({
                                value: office.id.toString(),
                                label: `${office.label} | ID: ${office.searchId}${
                                  office.status === "occupied"
                                    ? " (Ocupada)"
                                    : ""
                                }`,
                              }),
                            )}
                            shouldFilter={false}
                            disabled={!fields.contractType.value}
                          />
                          <p className="text-red-500 text-sm">
                            {fields.privateOfficeId.errors}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="w-full gap-6">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Dados da Reserva
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Preencha os detalhes contratuais referentes ao período,
                  consumo e valores negociados do contrato.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="self-stretch flex flex-col justify-start items-start gap-8">
                  <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-4">
                      <div className="self-stretch flex flex-col justify-start items-start gap-1">
                        <Label>Data de início *</Label>
                        <div className="self-stretch rounded-md flex flex-col justify-start items-start ">
                          <div className="w-full">
                            <Input
                              label="Data de início"
                              showlabel={false}
                              {...getInputProps(fields.startDate, {
                                type: "date",
                              })}
                              disable={
                                !selectedPrivateOffice &&
                                !poContractCreateData?.privateOfficeId &&
                                !selectedPackageId
                              }
                              onBlur={async (e) => {
                                calculateMinEndDate(e.target.value);
                                handleStartDateSelect(e.target.value);
                              }}
                            />
                            <p className="text-red-500 text-sm">
                              {startDateError || fields.startDate.errors}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="inline-flex justify-start items-center gap-2">
                        <input
                          type="checkbox"
                          id="hasUndeterminedTime"
                          name={fields.hasUndeterminedTime.name}
                          checked={isUndeterminedTime}
                          onChange={(e) => {
                            setIsUndeterminedTime(e.target.checked);
                            form.update({
                              name: fields.hasUndeterminedTime.name,
                              value: e.target.checked,
                            });
                            if (e.target.checked) {
                              form.update({
                                name: fields.endDate.name,
                                value: undefined,
                              });
                              if (endDateInputRef.current) {
                                endDateInputRef.current.value = "";
                              }
                            }
                          }}
                          className="h-4 w-4 shrink-0 accent-black"
                        />
                        <Label
                          htmlFor="hasUndeterminedTime"
                          className="text-sm font-medium leading-none cursor-pointer"
                        >
                          Tempo indeterminado
                        </Label>
                      </div>
                    </div>
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                      <Label>Data de término *</Label>
                      <div className="self-stretch rounded-md flex flex-col justify-start items-start">
                        <div className="w-full">
                          <Input
                            ref={endDateInputRef}
                            label="Data de término"
                            showlabel={false}
                            min={minEndDate || undefined}
                            defaultValue={fields.endDate.value}
                            {...getInputProps(fields.endDate, { type: "date" })}
                            disable={
                              Boolean(fields.hasUndeterminedTime.value) ||
                              !fields.startDate.value
                            }
                            error={fields.endDate.errors}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="self-stretch flex flex-col justify-start items-start">
                    <div className="self-stretch h-px border" />
                  </div>
                  <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                      <Label>Valor do parceiro R$*</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                        <div className="w-full">
                          <MoneyInput
                            label="Valor do parceiro"
                            placeholder="Adicione o valor do parceiro"
                            {...getInputProps(fields.partnerValue, {
                              type: "text",
                            })}
                            error={fields.partnerValue.errors}
                            onBlur={() => calculateMonthlyCredits()}
                            prefix="R$"
                          />
                        </div>
                      </div>
                      {!fields.partnerValue.errors && (
                        <div className="self-stretch justify-start text-zinc-500 text-sm font-normal">
                          Valor negociado com o parceiro, deve ser o mesmo que
                          consta no termo de cessão.
                        </div>
                      )}
                    </div>
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                      <Label>Créditos mensais *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                        <div className="w-full">
                          <Input
                            label="Créditos mensais"
                            showlabel={false}
                            placeholder="Digite o número de créditos mensais"
                            {...getInputProps(fields.monthlyCredits, {
                              type: "number",
                              step: "0.01",
                              onBlur: () =>
                                form.validate({
                                  name: fields.monthlyCredits.name,
                                }),
                            })}
                            onWheel={(e) => e.currentTarget.blur()}
                            disable={
                              !fields.startDate.value ||
                              !fields.partnerValue.value
                            }
                          />
                        </div>
                      </div>
                      {fields.monthlyCredits.errors && (
                        <p className="text-red-500 text-sm self-stretch justify-start">
                          {fields.monthlyCredits.errors}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="self-stretch inline-flex justify-start items-start gap-4">
                  <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                    <Label>Porcentagem de Take *</Label>
                    <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                      <div className="w-full">
                        <Input
                          label="Porcentagem de Take"
                          showlabel={false}
                          {...getInputProps(fields.takeRatePercentage, {
                            type: "number",
                          })}
                          onWheel={(e) => e.currentTarget.blur()}
                          error={fields.takeRatePercentage.errors}
                          onChange={(e) => {
                            form.value?.partnerValue &&
                              calculateRepass(Number(e.target.value));
                          }}
                          prefix="%"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                    <Label>Repasse</Label>
                    <div className="w-full">
                      <Input
                        label="Repasse"
                        showlabel={false}
                        placeholder="-"
                        {...getInputProps(fields.repass, {
                          type: "number",
                        })}
                        onWheel={(e) => e.currentTarget.blur()}
                        error={fields.repass.errors}
                        disable={
                          !fields.takeRatePercentage.value ||
                          !fields.partnerValue.value
                        }
                        readOnly={true}
                        className="bg-gray-100"
                        prefix="R$"
                      />
                    </div>
                    <div className="self-stretch justify-start text-zinc-500 text-sm font-normal">
                      Valor calculado automaticamente
                    </div>
                  </div>
                  <div className="flex-1 h-16 inline-flex flex-col justify-start items-start gap-1">
                    <Label>Número de pessoas *</Label>
                    <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                      <div className="w-full">
                        <Input
                          label="Número de pessoas"
                          showlabel={false}
                          placeholder="Digite o número de pessoas"
                          {...getInputProps(fields.members, {
                            type: "text",
                          })}
                        />
                      </div>
                      <p className="text-red-500 text-sm">
                        {fields.members.errors}
                      </p>
                    </div>
                  </div>
                </div>
                {isTakeRateException(fields.takeRatePercentage.value) && (
                  <TakeRateExceptionFields
                    justification={
                      fields[TAKE_RATE_EXCEPTION_FORM_FIELDS.justification]
                    }
                    approvedBy={
                      fields[TAKE_RATE_EXCEPTION_FORM_FIELDS.approvedBy]
                    }
                    approvedAt={
                      fields[TAKE_RATE_EXCEPTION_FORM_FIELDS.approvedAt]
                    }
                  />
                )}
              </CardContent>
            </Card>
            <Card className="w-full gap-6">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Avisos e Ajustes
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Configure regras específicas para aviso prévio e vigência.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="self-stretch flex flex-col justify-start items-start gap-4">
                  <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                      <Label>Período de Aviso Prévio do Cliente *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                        <div className="w-full">
                          <Input
                            label="Período de Aviso Prévio do Cliente"
                            showlabel={false}
                            defaultValue={90}
                            {...getInputProps(fields.noticeInDaysClient, {
                              type: "number",
                            })}
                            onWheel={(e) => e.currentTarget.blur()}
                            error={fields.noticeInDaysClient.errors}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                      <Label>Período de Aviso Prévio do Parceiro *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                        <div className="w-full">
                          <Input
                            label="Período de Aviso Prévio do Parceiro"
                            showlabel={false}
                            defaultValue={60}
                            {...getInputProps(fields.noticeInDaysPartner, {
                              type: "number",
                            })}
                            onWheel={(e) => e.currentTarget.blur()}
                            error={fields.noticeInDaysPartner.errors}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                      <Label>Vigência prevista (em meses) *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-[3px]">
                        <div className="w-full">
                          <Input
                            label="Vigência prevista (em meses)"
                            showlabel={false}
                            placeholder="Adicione a vigência prevista (em meses)"
                            {...getInputProps(fields.expectedDurationMonths, {
                              type: "number",
                            })}
                            onWheel={(e) => e.currentTarget.blur()}
                            error={fields.expectedDurationMonths.errors}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 h-16 inline-flex flex-col justify-start items-start gap-2">
                      <Label>Índice de reajuste *</Label>
                      <div className="self-stretch flex flex-col justify-start items-start gap-1">
                        <Select
                          key={fields.renewalIndicator.key}
                          name={fields.renewalIndicator.name}
                          defaultValue={fields.renewalIndicator.value as string}
                          onValueChange={(value) => {
                            form.update({
                              name: fields.renewalIndicator.name,
                              value,
                            });
                            const selectElement =
                              document.activeElement as HTMLElement;
                            if (selectElement) selectElement.blur();
                          }}
                        >
                          <SelectTrigger className="w-full h-10 px-3 py-2 text-sm border rounded-md bg-white border-[#d4d4d8] ring-inset">
                            <SelectValue placeholder="Selecione um índice de reajuste" />
                          </SelectTrigger>
                          <SelectContent>
                            {renewalIndicatorOptions.map((renewalIndicator) => (
                              <SelectItem
                                key={renewalIndicator.value}
                                value={renewalIndicator.value}
                              >
                                {renewalIndicator.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-red-500 text-sm">
                          {fields.renewalIndicator.errors}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="w-full gap-6">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Documentos
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Anexe os termos formais assinados entre a empresa contratante
                  e o parceiro.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="inline-flex justify-start items-start gap-4">
                  <Card className="bg-slate-50 w-full">
                    <CardHeader className="justify-center items-center">
                      <CardTitle className="text-xl">
                        Termo de Aceite *
                      </CardTitle>
                      <CardDescription className="min-h-[2.5rem]">
                        {`Certifique-se que o termo refere-se a empresa ${selectedCompanyName || ""}`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <PoAssetsDropzone
                        name={fields.acceptanceTerm.name}
                        onDrop={onAcceptanceTermDrop}
                        files={acceptanceTermFiles}
                        errors={fields.acceptanceTerm.errors}
                        className="bg-white"
                        isLoading={acceptanceTermLoading}
                        removeFile={removeAcceptanceTermFile}
                        hasFileRemoveButton={true}
                      />
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-50 w-full">
                    <CardHeader className="justify-center items-center">
                      <CardTitle className="text-xl">
                        Termo de Ciência *
                      </CardTitle>
                      <CardDescription className="min-h-[2.5rem]">
                        {`Certifique-se que o termo refere-se ao espaço ${selectedSpaceName || ""}`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <PoAssetsDropzone
                        name={fields.knowledgeTerm.name}
                        onDrop={onKnowledgeTermDrop}
                        files={knowledgeTermFiles}
                        errors={fields.knowledgeTerm.errors}
                        className="bg-white"
                        isLoading={knowledgeTermLoading}
                        removeFile={removeKnowledgeTermFile}
                        hasFileRemoveButton={true}
                      />
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
            <Card className="w-full gap-6">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Observações
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Registre observações relevantes para a operação.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="self-stretch inline-flex justify-start items-start gap-8">
                  <div className="flex-1 flex justify-start items-start gap-4">
                    <div className="flex-1 h-32 inline-flex flex-col justify-start items-start gap-2">
                      <Label>Notas</Label>
                      <Textarea
                        placeholder="Digite detalhes importantes acerca desta reserva."
                        className="h-32 bg-white border-[#d4d4d8] ring-inset"
                        {...getInputProps(fields.notes, { type: "text" })}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Escolha de que forma será feita esta cobrança.
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Você pode realizar esta reserva sem alterar os ciclos do
                  pacote, ou optar por ajustá-los para realizar um upsell.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                <RadioGroup
                  name={fields.flowType.name}
                  value={fields.flowType.value}
                  onValueChange={(value) => {
                    form.update({ name: fields.flowType.name, value });
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem id="flow_type_default" value="default" />
                    <Label
                      htmlFor="flow_type_default"
                      className="cursor-pointer"
                    >
                      Criar contrato sem editar ciclos.
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      id="flow_type_upsell"
                      value="upsell"
                      disabled={packageDetails?.duration !== "monthly"}
                    />
                    <Label
                      htmlFor="flow_type_upsell"
                      className={`cursor-pointer ${packageDetails?.duration !== "monthly" ? "text-gray-400 cursor-not-allowed" : ""}`}
                    >
                      Editar ciclos e ajustar créditos.
                    </Label>
                  </div>
                </RadioGroup>

                {fields.flowType.value === "default" &&
                  activeCycle?.credits?.amount &&
                  Number(fields.monthlyCredits?.value) &&
                  activeCycle.credits.amount < creditConsumption &&
                  packageDetails?.duration === "monthly" && (
                    <Alert className="bg-amber-50 rounded-lg border border-yellow-500/50 w-fullgap-1 inline-flex mt-4">
                      <div className="flex gap-3  items-center pl-6">
                        <div className="absolute top-4 left-4">
                          <SirenIcon
                            className="text-yellow-900"
                            width={16}
                            height={16}
                          />
                        </div>
                        <div>
                          <AlertTitle className="text-base font-medium text-yellow-900 mb-0">
                            Atenção
                          </AlertTitle>
                          <AlertDescription className="self-stretch text-yellow-900 text-sm font-normal leading-tight">
                            Os créditos mensais do contrato ultrapassam os
                            créditos disponíveis nos ciclos. Recomendamos editar
                            os ciclos e ajustar os créditos para evitar que os
                            ciclos excedam ao criar este contrato. Se isso for
                            uma exceção permitida, você pode prosseguir com a
                            criação do contrato normalmente.
                          </AlertDescription>
                        </div>
                      </div>
                    </Alert>
                  )}
              </CardContent>
            </Card>
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-8">
            <div className="self-stretch inline-flex justify-end items-start gap-2">
              <Button
                variant="outline"
                className="px-4 py-2"
                onClick={() => navigate(DEFAULT_ROUTES.LICENSE_FEE_LIST)}
              >
                Voltar
              </Button>
              <Button
                type="submit"
                className="px-4 py-2"
                disabled={!form.valid || !!dealIdError || !activeCycle}
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                {form.value?.flowType === "upsell"
                  ? "Avançar para pagamentos"
                  : "Avançar para revisão"}
              </Button>
            </div>
          </div>
        </Form>

        <Card className="bg-slate-50 mt-[110px] inline-flex flex-col justify-start items-start gap-2 min-w-[400px] sticky top-16">
          {!packageDetails && !activeCycle && (
            <CardHeader>
              <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                Pacote e Ciclo
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Escolha um pacote e uma data de início para ver mais
                informações.
              </CardDescription>
            </CardHeader>
          )}
          {packageDetails && (
            <>
              <CardHeader className="pb-0 pt-2 px-3">
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Pacote
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 w-full">
                <div className="w-full border rounded-md overflow-hidden">
                  <div className="flex w-full">
                    <div className="flex-1 p-3 border-r border-border">
                      <div className="flex flex-col gap-1">
                        <div className="text-sm font-semibold">
                          {packageDetails?.id}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          ID do pacote
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 p-3 border-r border-border">
                      <div className="flex flex-col gap-1">
                        <div className="text-sm font-semibold">
                          {packageDetails?.startDate}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Data de início
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 p-3">
                      <div className="flex flex-col gap-1">
                        <div className="text-sm font-semibold">
                          {packageDetails?.endDate}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Data de fim
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-full border-t border-border">
                    <div className="w-full p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {packageDetails?.products
                          ?.map((product) => ConvertProductToName(product))
                          .join(", ")}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Produtos
                      </div>
                    </div>
                  </div>

                  <div className="w-full border-t border-border">
                    <div className="w-full p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {packageDetails?.groups
                          ?.map((group) => group.name)
                          .join(", ") || "Company wide"}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Grupos
                      </div>
                    </div>
                  </div>

                  <div className="w-full border-t border-border">
                    <div className="w-full p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {packageDetails?.duration
                          ? ConvertPackageDuration(packageDetails.duration) ||
                            "Mensal"
                          : "Mensal"}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Duração do ciclo
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </>
          )}
          {activeCycle && (
            <>
              <CardHeader className="pb-0 pt-2 px-3">
                <CardTitle className="text-bg-black text-2xl font-medium leading-relaxed">
                  Ciclo
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 w-full">
                <div className="w-full border rounded-md overflow-hidden">
                  <div className="grid grid-cols-3 divide-x divide-border">
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {activeCycle?.id}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        ID do ciclo
                      </div>
                    </div>
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {formatInTimeZone(
                          activeCycle?.startDate,
                          "UTC",
                          "dd/MM/yyyy",
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Data de início
                      </div>
                    </div>
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {formatInTimeZone(
                          activeCycle?.endDate,
                          "UTC",
                          "dd/MM/yyyy",
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Data de fim
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 divide-x divide-border border-t border-border">
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {activeCycle?.credits.amount} créditos
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Quantidade de créditos
                      </div>
                    </div>
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {Math.max(0, availableCycleCredits)} créditos
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Créditos disponíveis
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 divide-x divide-border border-t border-border">
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {creditConsumption} créditos
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Utilizados por PO
                      </div>
                    </div>
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        R$ {activeCycle?.credits.unitPrice.toFixed(2)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Valor unitário do crédito
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 divide-x divide-border border-t border-border">
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        R$ {activeCycle?.credits.exceededPrice.toFixed(2)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Valor do excedente
                      </div>
                    </div>
                    <div className="p-3 flex flex-col gap-1">
                      <div className="text-sm font-semibold">
                        {activeCycle?.credits.courtesy} créditos
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Cortesias
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
